import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Maximize2, X, MapPin, Lock, Pencil, ExternalLink, Crosshair } from 'lucide-react';
import { API, AuthContext } from '@/App';

// Free, keyless geofence editor. Uses the SAME map stack as Route Planning: Leaflet +
// OpenStreetMap / Esri World Imagery tiles (no API key) and the server-side
// Nominatim -> Geoapify geocoder (keys stay in the backend). The pin is LOCKED by default:
// panning / zooming / clicking never move it. Management must press "Edit Pin" to drag it,
// then "Save and Lock Location" (persisted on the backend before the UI locks). Boundaries
// are protected the same way behind "Edit Boundaries". Nothing a lookup returns replaces a
// saved position until it is explicitly accepted.

export function locationAddress(location) {
  return [location.address, location.city, location.state, location.zip].filter(Boolean).join(', ');
}

const ARRIVAL = '#5FA718';
const DEPARTURE = '#1C4FC9';
const PROPOSAL = '#E8820C';
const FL_CENTER = [27.8, -81.7];
const BOUND_KEYS = ['arrival_radius_m', 'departure_radius_m', 'departure_delay_seconds', 'max_accuracy_m', 'short_visit_review_minutes'];

const n = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const hasPt = (g) => n(g.latitude) !== null && n(g.longitude) !== null;

const handleIcon = (color) => L.divIcon({
  className: '',
  html: `<div style="background:${color};width:20px;height:20px;border-radius:50%;border:3px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.6);cursor:grab"></div>`,
  iconSize: [20, 20], iconAnchor: [10, 10],
});
const pinIcon = (locked) => L.divIcon({
  className: '',
  html: `<div style="background:${locked ? '#0E2A47' : '#C026D3'};width:18px;height:18px;border-radius:50%;border:3px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.6);cursor:${locked ? 'default' : 'move'}"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});
const proposalIcon = L.divIcon({
  className: '',
  html: `<div style="background:${PROPOSAL};width:16px;height:16px;border-radius:50%;border:3px dashed #fff;box-shadow:0 1px 5px rgba(0,0,0,.6)"></div>`,
  iconSize: [16, 16], iconAnchor: [8, 8],
});

// Destination point from a center, given distance (m) and bearing (deg). Used to place each
// resize handle on a distinct side (arrival east, departure north) so they never overlap.
const edgeAt = (lat, lng, radius, bearing) => {
  const b = (bearing ?? 90) * Math.PI / 180;
  const dLat = (radius * Math.cos(b)) / 111320;
  const dLng = (radius * Math.sin(b)) / (111320 * Math.cos(lat * Math.PI / 180));
  return L.latLng(lat + dLat, lng + dLng);
};

export default function ServiceGeofenceMap({ location, value, onChange, locationId, onPersisted }) {
  const { token } = useContext(AuthContext);
  // A single, persistent Leaflet container DOM node we physically move between the inline
  // host and the full-screen portal host (so Leaflet never remounts and full-screen escapes
  // the Radix dialog's CSS transform).
  const mapEl = useRef(null);
  if (!mapEl.current && typeof document !== 'undefined') {
    const d = document.createElement('div');
    d.style.width = '100%'; d.style.height = '100%';
    d.setAttribute('data-testid', 'loc-geofence-map');
    d.setAttribute('aria-label', 'Service-location geofence map');
    mapEl.current = d;
  }
  const inlineHost = useRef(null);
  const fsHost = useRef(null);
  const objects = useRef(null);
  const current = useRef(value);
  const change = useRef(onChange);
  const pinModeRef = useRef('locked');
  current.current = value;
  change.current = onChange;

  const [saved, setSaved] = useState(() => ({ ...value }));
  const savedAddr = useRef(locationAddress(location));
  const [pinMode, setPinMode] = useState('locked');     // 'locked' | 'edit'
  const [proposal, setProposal] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [expanded, setExpanded] = useState(false);
  pinModeRef.current = pinMode;

  const address = locationAddress(location);
  const hasPoint = hasPt(value);
  const samePin = n(value.latitude) === n(saved.latitude) && n(value.longitude) === n(saved.longitude);
  const savedHasPoint = hasPt(saved);
  const addressChanged = savedHasPoint && address !== savedAddr.current;
  const gmapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([location.location_name, address].filter(Boolean).join(' '))}`;

  const emit = (patch) => change.current({ ...current.current, ...patch });

  // Build the map once.
  useEffect(() => {
    if (objects.current) return;
    if (inlineHost.current && mapEl.current.parentNode !== inlineHost.current) {
      inlineHost.current.appendChild(mapEl.current);
    }
    const map = L.map(mapEl.current, { scrollWheelZoom: true }).setView(FL_CENTER, 6);
    const sat = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Imagery &copy; Esri, Maxar, Earthstar Geographics', maxZoom: 20,
    }).addTo(map);
    const street = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors', maxZoom: 19,
    });
    L.control.layers({ Satellite: sat, Street: street }, {}, { position: 'topright' }).addTo(map);

    const departure = L.circle(FL_CENTER, { radius: 200, color: DEPARTURE, weight: 2, fillColor: DEPARTURE, fillOpacity: 0.08 });
    const arrival = L.circle(FL_CENTER, { radius: 100, color: ARRIVAL, weight: 2, fillColor: ARRIVAL, fillOpacity: 0.14 });
    const pin = L.marker(FL_CENTER, { icon: pinIcon(true), draggable: false, autoPan: true, autoPanPadding: L.point(60, 60) });
    const aHandle = L.marker(FL_CENTER, { icon: handleIcon(ARRIVAL), draggable: true });
    const dHandle = L.marker(FL_CENTER, { icon: handleIcon(DEPARTURE), draggable: true });
    const proposalMarker = L.marker(FL_CENTER, { icon: proposalIcon });

    const centerLatLng = () => L.latLng(Number(current.current.latitude), Number(current.current.longitude));
    // A map click only PLACES the first pin while editing; it never relocates a placed pin.
    map.on('click', (e) => {
      if (pinModeRef.current === 'edit' && !hasPt(current.current)) emit({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    });
    // Pin: drag smoothly with NO per-move React state (that was snapping it back). Move the
    // circles/handles WITH the pin (radii preserved); commit the final coordinates once on
    // dragend so the displayed lat/lng update when the drag finishes.
    pin.on('dragstart', () => { objects.current.interacting = 'pin'; });
    pin.on('drag', () => {
      const o = objects.current; const ll = o.pin.getLatLng();
      const ar = o.arrival.getRadius(), dr = o.departure.getRadius();
      o.arrival.setLatLng(ll); o.departure.setLatLng(ll);
      o.aHandle.setLatLng(edgeAt(ll.lat, ll.lng, ar, 90));
      o.dHandle.setLatLng(edgeAt(ll.lat, ll.lng, dr, 180));
    });
    pin.on('dragend', () => {
      const o = objects.current; o.interacting = null;
      const ll = o.pin.getLatLng();
      emit({ latitude: Number(ll.lat.toFixed(7)), longitude: Number(ll.lng.toFixed(7)) });
    });
    // Boundary handles are always draggable — resize anytime, no mode switch. The handle
    // being dragged is never snapped back (the sync effect skips it via objects.interacting).
    aHandle.on('dragstart', () => { objects.current.interacting = 'arrival'; });
    aHandle.on('drag', () => {
      const o = objects.current;
      const r = Math.max(25, Math.min(2000, Math.round(centerLatLng().distanceTo(o.aHandle.getLatLng()))));
      o.arrival.setRadius(r);
      const dr = Math.max(r, Number(current.current.departure_radius_m) || r);
      o.departure.setRadius(dr);
      emit({ arrival_radius_m: r, departure_radius_m: dr });
    });
    aHandle.on('dragend', () => { objects.current.interacting = null; });
    dHandle.on('dragstart', () => { objects.current.interacting = 'departure'; });
    dHandle.on('drag', () => {
      const o = objects.current;
      const minR = Number(current.current.arrival_radius_m) || 25;
      const r = Math.max(minR, Math.min(5000, Math.round(centerLatLng().distanceTo(o.dHandle.getLatLng()))));
      o.departure.setRadius(r);
      emit({ departure_radius_m: r });
    });
    dHandle.on('dragend', () => { objects.current.interacting = null; });

    objects.current = { map, arrival, departure, pin, aHandle, dHandle, proposalMarker, syncing: false, centered: false, interacting: null };
    setReady(true);
    setTimeout(() => map.invalidateSize(), 150);
    return () => { map.remove(); objects.current = null; };
  }, []);

  // Reflect form values + modes onto the map.
  useEffect(() => {
    const o = objects.current;
    if (!o) return;
    o.syncing = true;
    const show = (layer, on) => { if (on) { if (!o.map.hasLayer(layer)) layer.addTo(o.map); } else if (o.map.hasLayer(layer)) o.map.removeLayer(layer); };
    if (hasPoint) {
      const lat = Number(value.latitude), lng = Number(value.longitude);
      const center = L.latLng(lat, lng);
      const ar = Math.max(25, Number(value.arrival_radius_m) || 100);
      const dr = Math.max(ar, Number(value.departure_radius_m) || 200);
      const busyWith = o.interacting;  // don't fight an in-progress drag
      o.arrival.setLatLng(center).setRadius(ar);
      o.departure.setLatLng(center).setRadius(dr);
      o.pin.setIcon(pinIcon(pinMode === 'locked'));
      if (busyWith !== 'pin') o.pin.setLatLng(center);
      if (busyWith !== 'arrival') o.aHandle.setLatLng(edgeAt(lat, lng, ar, 90));
      if (busyWith !== 'departure') o.dHandle.setLatLng(edgeAt(lat, lng, dr, 180));
      show(o.arrival, true); show(o.departure, true); show(o.pin, true);
      show(o.aHandle, true); show(o.dHandle, true);
      if (o.pin.dragging) { pinMode === 'edit' ? o.pin.dragging.enable() : o.pin.dragging.disable(); }
      if (!o.centered) { o.map.fitBounds(o.departure.getBounds().pad(0.35)); o.centered = true; }
    } else {
      [o.arrival, o.departure, o.pin, o.aHandle, o.dHandle].forEach((l) => show(l, false));
    }
    o.syncing = false;
  }, [value, hasPoint, ready, pinMode]);

  // Proposal marker (shown separately from the confirmed pin until accepted).
  useEffect(() => {
    const o = objects.current;
    if (!o) return;
    if (proposal) {
      o.proposalMarker.setLatLng([proposal.lat, proposal.lng]);
      if (!o.map.hasLayer(o.proposalMarker)) o.proposalMarker.addTo(o.map);
      o.map.setView([proposal.lat, proposal.lng], 18);
    } else if (o.map.hasLayer(o.proposalMarker)) {
      o.map.removeLayer(o.proposalMarker);
    }
  }, [proposal]);

  // Move the single map node between inline and full-screen hosts, then recalc size.
  useEffect(() => {
    const host = expanded ? fsHost.current : inlineHost.current;
    if (host && mapEl.current && mapEl.current.parentNode !== host) host.appendChild(mapEl.current);
    if (objects.current) setTimeout(() => objects.current && objects.current.map.invalidateSize(), 80);
  }, [expanded, ready]);

  // Fit the whole departure circle into view (and scroll the map into center) whenever the
  // editor is shown, so BOTH resize handles (arrival east, departure south) are reachable.
  useEffect(() => {
    const o = objects.current;
    if (!o || !ready || !hasPt(current.current)) return;
    if (!expanded && mapEl.current) mapEl.current.scrollIntoView({ block: 'center' });
    setTimeout(() => {
      if (!o.map.hasLayer(o.departure)) return;
      o.map.invalidateSize();
    }, 140);
  }, [expanded, ready]);

  useEffect(() => {
    setMessage(!hasPoint
      ? (pinMode === 'edit' ? 'Click the property on the map to drop the pin, then drag it to the exact building.' : 'No position set. Press "Edit Pin" (or "Locate Stored Address") to place the property pin.')
      : pinMode === 'edit' ? 'Drag the navy/magenta pin onto the exact building, then Save. Clicking elsewhere will not move a placed pin.'
        : 'Pin is locked. Drag the green/blue edge handles to resize the circles (or type meters below). Press "Edit Pin" to move the pin.');
  }, [hasPoint, pinMode]);

  // While the pin is locked, keep the saved baseline's pin equal to the persisted value.
  // This absorbs the fresh copy LocationForm fetches on open and avoids a false chip.
  useEffect(() => {
    if (pinMode === 'locked') setSaved((prev) => ({ ...prev, latitude: value.latitude, longitude: value.longitude }));
  }, [value.latitude, value.longitude, pinMode]);

  // While full-screen, Escape should only collapse the map — not close the whole dialog.
  // Use window capture + stopImmediatePropagation so we win over Radix's dismiss handler.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); e.stopPropagation(); setExpanded(false); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [expanded]);

  async function locate() {
    if (!address) return;
    setBusy(true); setMessage('Finding the stored address…');
    try {
      const r = await axios.post(`${API}/service-locations/geocode-address`, { address },
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.data?.found) { setMessage('Address not found. Check the stored address, or press "Edit Pin" and place the pin manually.'); return; }
      setProposal(r.data);  // held separately — does NOT replace the confirmed pin
      setMessage('Review the suggested position below. It is a lookup result, not a confirmed building.');
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Address lookup is unavailable right now. Press "Edit Pin" to place the pin manually.');
    } finally { setBusy(false); }
  }

  function useProposal() {
    if (!proposal) return;
    setPinMode('edit');
    emit({ latitude: proposal.lat, longitude: proposal.lng });
    setProposal(null);
    setMessage('Suggested position applied as an UNSAVED pin. Confirm the building, then Save and Lock.');
  }

  function editPin() { setPinMode('edit'); }
  function cancelPin() {
    if (!samePin && !window.confirm('Discard the unsaved pin position and restore the last saved location?')) return;
    emit({ latitude: saved.latitude ?? '', longitude: saved.longitude ?? '' });
    setPinMode('locked');
  }
  async function saveLock() {
    if (!hasPoint) { toast.error('Drop the pin on the property first'); return; }
    if (n(value.departure_radius_m) < n(value.arrival_radius_m)) { toast.error('Departure radius must be at least the arrival radius'); return; }
    if (!locationId) { toast.error('Save the location first, then lock the pin'); return; }
    setBusy(true);
    try {
      const body = { latitude: n(value.latitude), longitude: n(value.longitude) };
      BOUND_KEYS.forEach((k) => { const v = n(value[k]); if (v !== null) body[k] = v; });
      const r = await axios.patch(`${API}/service-locations/${locationId}/geofence`, body,
        { headers: { Authorization: `Bearer ${token}` } });
      const geo = r.data.geofence || {};
      setSaved((p) => ({ ...p, ...geo }));
      savedAddr.current = address;
      emit(geo);
      setPinMode('locked');
      onPersisted?.();
      toast.success('Geofence saved and pin locked');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save — changes kept as unsaved');
    } finally { setBusy(false); }
  }

  const toggleEnabled = async (checked) => {
    if (checked && !savedHasPoint) { toast.error('Save and lock the pin before enabling verification'); return; }
    emit({ enabled: checked });
    if (locationId) {
      try {
        await axios.patch(`${API}/service-locations/${locationId}/geofence`, { enabled: checked },
          { headers: { Authorization: `Bearer ${token}` } });
        setSaved((p) => ({ ...p, enabled: checked }));
        onPersisted?.();
      } catch (e) {
        emit({ enabled: !checked });
        toast.error(e.response?.data?.detail || 'Could not update verification status');
      }
    }
  };

  const pinChip = !hasPoint
    ? { text: 'No position set', cls: 'bg-slate-100 text-slate-600' }
    : (!samePin || pinMode === 'edit')
      ? { text: 'Unsaved position', cls: 'bg-amber-100 text-amber-800' }
      : { text: 'Saved & locked', cls: 'bg-emerald-100 text-emerald-800' };

  const btn = 'inline-flex items-center gap-1.5 min-h-10 rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-40';
  const numInput = (key, label, min, max, step = '1') => (
    <label key={key} className="space-y-1 block">
      <span className="text-xs text-slate-600">{label}</span>
      <input type="number" data-testid={`loc-geofence-${key}`} step={step} min={min} max={max}
        disabled={['latitude', 'longitude'].includes(key) ? pinMode !== 'edit' : false}
        value={value[key] ?? ''} onChange={(e) => emit({ [key]: e.target.value })}
        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500" />
    </label>
  );

  const reviewBanner = addressChanged ? (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="loc-geofence-review-flag">
      The stored address changed after this position was confirmed. Re-check the pin against the new address — it was not moved automatically.
    </div>
  ) : null;

  const pinControlsBar = (
    <div className="flex flex-wrap items-center gap-2">
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${pinChip.cls}`} data-testid="loc-geofence-pin-status">
        {pinChip.text === 'Saved & locked' ? <Lock className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}{pinChip.text}
      </span>
      {pinMode === 'locked' ? (
        <button type="button" className={btn} onClick={editPin} data-testid="loc-geofence-edit-pin"><Pencil className="h-4 w-4" />Edit Pin</button>
      ) : (
        <>
          <button type="button" className={`${btn} border-[#0E2A47] bg-[#0E2A47] text-white`} disabled={busy || !hasPoint} onClick={saveLock} data-testid="loc-geofence-save-lock"><Lock className="h-4 w-4" />Save and Lock Location</button>
          <button type="button" className={btn} onClick={cancelPin} data-testid="loc-geofence-cancel-pin">Cancel</button>
        </>
      )}
      <button type="button" className={btn} disabled={!ready || !address || busy} onClick={locate} data-testid="loc-geofence-locate"><Crosshair className="h-4 w-4" />{busy ? 'Finding…' : 'Locate Stored Address'}</button>
      <a className={btn} href={gmapsHref} target="_blank" rel="noopener noreferrer" data-testid="loc-geofence-open-gmaps"><ExternalLink className="h-4 w-4" />Open in Google Maps</a>
    </div>
  );

  const proposalCard = proposal ? (
    <div className="rounded-lg border border-orange-300 bg-orange-50 p-3 text-sm" data-testid="loc-geofence-proposal">
      <p className="font-semibold text-orange-900">Suggested position (lookup result — confirm before saving)</p>
      <p className="text-orange-900">{proposal.matched_address}</p>
      <p className="mt-1 text-xs">
        Match quality: <strong>{proposal.match_quality === 'rooftop' ? 'Building-level' : 'Approximate (street/area) — NOT a verified building'}</strong>
        {proposal.confidence != null ? ` · confidence ${Math.round(proposal.confidence * 100)}%` : ''}
      </p>
      <div className="mt-2 flex gap-2">
        <button type="button" className={`${btn} border-orange-500 bg-orange-500 text-white`} onClick={useProposal} data-testid="loc-geofence-use-proposal">Use this position (unsaved)</button>
        <button type="button" className={btn} onClick={() => { setProposal(null); setMessage(''); }} data-testid="loc-geofence-dismiss-proposal">Dismiss</button>
      </div>
    </div>
  ) : null;

  const boundsHeader = (
    <div className="flex flex-wrap items-center gap-2">
      <p className="text-sm">
        <span className="font-semibold text-[#5FA718]">Arrival {hasPoint ? `(${Math.round(Number(value.arrival_radius_m) || 0)} m)` : ''}</span>
        {' · '}
        <span className="font-semibold text-[#1C4FC9]">Departure {hasPoint ? `(${Math.round(Number(value.departure_radius_m) || 0)} m)` : ''}</span>
      </p>
      <span className="text-xs text-slate-500">Drag the green (east) / blue (south) edge handles to resize, or type meters below.</span>
    </div>
  );

  const statusLine = <p role="status" className="text-sm text-slate-600" data-testid="loc-geofence-status">{message}</p>;

  return (
    <div className="md:col-span-2 space-y-3">
      <div data-testid="loc-geofence-header">
        <p className="text-sm font-bold text-[#0E2A47]" data-testid="loc-geofence-name">{location.location_name || 'Service location'}</p>
        <p className="text-sm text-slate-600" data-testid="loc-geofence-address">{address || 'This location has no stored address.'}</p>
      </div>

      {!expanded && reviewBanner}
      {!expanded && pinControlsBar}
      {!expanded && proposalCard}

      <div className="relative">
        <div ref={inlineHost} className={expanded ? 'hidden' : 'w-full rounded-xl border bg-slate-100 h-[32rem] md:h-[640px]'} />
        {!expanded && (
          <button type="button" onClick={() => setExpanded(true)} data-testid="loc-geofence-expand"
            className="absolute right-3 top-3 z-[1000] inline-flex items-center gap-1 rounded-lg bg-white/95 px-3 py-2 text-sm font-semibold text-[#0E2A47] shadow-lg"><Maximize2 className="h-4 w-4" />Expand</button>
        )}
      </div>
      {expanded && createPortal(
        <div className="fixed inset-0 z-[9999] flex flex-col bg-white pointer-events-auto">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b px-4 py-2">
            <span className="truncate text-sm font-bold text-[#0E2A47]">{location.location_name || 'Service location'} — geofence map</span>
            <button type="button" onClick={() => setExpanded(false)} data-testid="loc-geofence-close-expand"
              className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-[#0E2A47] px-3 py-2 text-sm font-semibold text-white"><X className="h-4 w-4" />Close</button>
          </div>
          <div className="shrink-0 max-h-[42vh] space-y-2 overflow-auto border-b bg-slate-50 px-4 py-2">
            {reviewBanner}
            {pinControlsBar}
            {boundsHeader}
            {proposalCard}
            {statusLine}
          </div>
          <div ref={fsHost} className="min-h-0 flex-1" />
        </div>, document.body)}

      {!expanded && statusLine}

      {/* Boundaries */}
      <div className="rounded-lg border border-slate-200 p-3 space-y-3">
        {!expanded && boundsHeader}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {numInput('latitude', 'Map latitude', -90, 90, 'any')}
          {numInput('longitude', 'Map longitude', -180, 180, 'any')}
          {numInput('arrival_radius_m', 'Arrival radius (meters)', 25, 2000)}
          {numInput('departure_radius_m', 'Departure radius (meters)', 25, 5000)}
          {numInput('departure_delay_seconds', 'Departure confirmation delay (seconds)', 30, 1800)}
          {numInput('max_accuracy_m', 'Maximum GPS uncertainty (meters)', 5, 500)}
          {numInput('short_visit_review_minutes', 'Short-visit review threshold (minutes, optional)', 1, 1440)}
        </div>
        <p className="text-xs text-slate-500">In <strong>Edit Boundaries</strong> mode, drag the round handles on the map — green (east) resizes arrival, blue (south) resizes departure — or type exact meters below; the circles and numbers stay in sync. Departure must be at least as large as arrival; use a departure boundary that includes the parking lot. Latitude/longitude edit with "Edit Pin". These settings do not calculate pay.</p>
      </div>

      {/* Enable verification */}
      <div className="space-y-1">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="loc-geofence-enabled" checked={!!value.enabled}
            onChange={(e) => toggleEnabled(e.target.checked)} /> Enable GPS service verification at this location
        </label>
        <p className="text-xs text-slate-500">Turns on the geofence arrival/departure check for the boundary set above (the <strong>where</strong>). <strong>Who</strong> can start a visit comes from Admin Hub assignments. This toggle controls only the geofence boundary check and requires a saved, locked pin.</p>
        <p className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600" data-testid="loc-phone-monitoring-note"><strong>Phone monitoring (separate):</strong> each assigned worker's installed TEAM app reports location during an active visit. This checkbox activates only the location-boundary check — it does not install or connect phone monitoring.</p>
      </div>
    </div>
  );
}
